import { UserIcon } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { PageBody, PageHeader } from '@/components/AppShell'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute } from '@/lib/format'

import { itemsOf, useStaffUsers } from '../api'
import { LoadMore, StaffGate, StateBadge } from '../ui'

export function StaffUsersPage() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [applied, setApplied] = useState('')
  const list = useStaffUsers(applied)
  const rows = itemsOf(list.data)

  return (
    <StaffGate>
      <PageHeader eyebrow="Staff" title="Users" description="Search by email, name, or an exact id." />
      <PageBody>
        <Card>
          <CardContent className="space-y-4">
            <form
              className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]"
              onSubmit={(e) => {
                e.preventDefault()
                setApplied(q)
              }}
            >
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="usr-q">Search</Label>
                <Input id="usr-q" value={q} onChange={(e) => { setQ(e.target.value) }} />
              </div>
              <div className="flex items-end">
                <Button type="submit">Search</Button>
              </div>
            </form>

            {list.isError ? (
              <ErrorState error={list.error} title="Could not load users" onRetry={() => void list.refetch()} />
            ) : list.isPending ? (
              <TableSkeleton rows={6} columns={5} />
            ) : rows.length === 0 ? (
              <EmptyState icon={UserIcon} title="No users match" description="Try another search." />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>Verified</TableHead>
                        <TableHead className="text-right">Accounts</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((u) => (
                        <TableRow
                          key={u.id}
                          data-testid="staff-user-row"
                          className="cursor-pointer"
                          onClick={() => { void navigate(`/app/staff/users/${u.id}`) }}
                        >
                          <TableCell className="max-w-[18rem] truncate font-mono text-xs">{u.email}</TableCell>
                          <TableCell className="max-w-[12rem] truncate">{u.name ?? '—'}</TableCell>
                          <TableCell><StateBadge state={u.user_state} /></TableCell>
                          <TableCell>{u.email_verified ? <Badge variant="success">yes</Badge> : <Badge variant="outline">no</Badge>}</TableCell>
                          <TableCell className="text-right tabular-nums">{u.account_count}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatAbsolute(u.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <LoadMore shown={rows.length} hasNext={list.hasNextPage} loading={list.isFetchingNextPage} onMore={() => void list.fetchNextPage()} />
              </>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </StaffGate>
  )
}
